import './ToggleWishlist.scss';

import cn from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EVENT_CODE } from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import AddToWishlist from '@/ebat/components/blocks/Modal/Common/AddToWishlist/AddToWishlist';
import { ReactComponent as FavoriteSvg } from '@/materiaux/assets/icons/favorite.svg';
import { ReactComponent as FavoriteActiveSvg } from '@/materiaux/assets/icons/favorite-active.svg';

import {
  ToggleWishlistDefaultProps,
  ToggleWishlistPropTypes,
} from './ToggleWishlist.proptypes';

const ToggleWishlist = ({
  className,
  disabled,
  id,
  isFavorite,
  isLoading,
  showLabel,
  sku,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { onOpenModal, setModalClassName, setModalContent } = useModalContext();

  const [favorite, setFavorite] = useState(isFavorite);

  const isDisabled = useMemo(() => {
    const blocked = isLoading || disabled;

    return {
      disabled: blocked,
      loading: isLoading,
      manage:
        blocked ||
        !user?.functionality_accesses?.wishlists ||
        !user?.functionality_accesses?.wishlists_add,
    };
  }, [disabled, isLoading, user]);

  const handleClick = useCallback(() => {
    setModalContent(
      <AddToWishlist
        disabled={isDisabled.manage}
        isLoading={isDisabled.loading}
        items={[{ sku }]}
        product={{ id }}
        setFavorite={setFavorite}
      />,
    );
    setModalClassName('modal-add-to-wishlist');
    onOpenModal();
  }, [id, isDisabled, onOpenModal, setModalClassName, setModalContent, sku]);

  const handleKeyUp = useCallback(
    (e) => {
      if (e.code === EVENT_CODE.SPACE) {
        setModalContent(
          <AddToWishlist
            disabled={isDisabled.manage}
            isLoading={isDisabled.loading}
            items={[{ sku }]}
            product={{ id }}
            setFavorite={setFavorite}
          />,
        );
        setModalClassName('modal-add-to-wishlist');
        onOpenModal();
      }
    },
    [setModalContent, sku, id, isDisabled, setModalClassName, onOpenModal],
  );

  return (
    <div
      className={cn('toggle-favorite', className, {
        'toggle-favorite--disabled': isDisabled.disabled,
        active: favorite,
      })}
      data-qa-id={
        isDisabled.disabled
          ? 'toggle-favorite-disabled'
          : 'toggle-favorite-active'
      }
    >
      <input
        className="toggle-favorite__input"
        data-qa-id={cn('toggle-favorite-input', { checked: favorite })}
        defaultChecked={favorite}
        disabled={isDisabled.manage}
        id={`toggle-favorite-${id}`}
        name={`toggle-favorite-${id}`}
        type="checkbox"
      />
      <label
        className="toggle-favorite__label"
        data-qa-id="toggle-favorite-label"
        htmlFor={`toggle-favorite-${id}`}
        onClick={handleClick}
        onKeyUp={handleKeyUp}
        role="presentation"
      >
        {favorite ? (
          <FavoriteActiveSvg
            className="toggle-favorite__icon toggle-favorite__icon--active"
            data-qa-id="icon-active"
          />
        ) : (
          <FavoriteSvg
            className="toggle-favorite__icon"
            data-qa-id="icon-inactive"
          />
        )}
        {showLabel && (
          <span className="toggle-favorite__text">
            {favorite
              ? `${t('common:messages:actions.wishlist.added')}`
              : `${t('common:messages:actions.wishlist.add')}`}
          </span>
        )}
      </label>
    </div>
  );
};

ToggleWishlist.defaultProps = {
  ...ToggleWishlistDefaultProps,
};

ToggleWishlist.propTypes = {
  ...ToggleWishlistPropTypes,
};

export default ToggleWishlist;
